import { useState } from "react";
import axios from "axios";
import { CIRCLE } from "@/constants/testIds";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Lifestyle & Wellness",
  "Technology & AI",
  "Business & Leadership",
  "Creativity & Culture",
  "Community & Networking",
  "Corporate Events",
  "Hosting / Partnership",
];

const FORMATS = ["Online", "In-person", "Hybrid", "No preference"];

const slug = (s) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function CircleForm() {
  const [state, setState] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    interests: [],
    preferred_format: "No preference",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const toggleInterest = (v) => {
    setState((s) => ({
      ...s,
      interests: s.interests.includes(v)
        ? s.interests.filter((i) => i !== v)
        : [...s.interests, v],
    }));
  };

  const validate = () => {
    const e = {};
    if (!state.name.trim()) e.name = "Please share your name";
    if (!state.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email))
      e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/circle`, state);
      setSuccess(true);
      toast.success("Welcome to the Aura Circle.", {
        description:
          "You'll receive updates on upcoming Aura events and experiences.",
      });
      setState({
        name: "",
        email: "",
        phone: "",
        city: "",
        interests: [],
        preferred_format: "No preference",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="circle" className="py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="rounded-3xl bg-[var(--aura-charcoal)] text-[var(--aura-ivory)] p-8 md:p-14 relative overflow-hidden">
          <div
            className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-30 blur-3xl pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(0,82,255,0.7), transparent 60%)",
            }}
          />
          <div
            className="absolute -bottom-24 -left-24 w-[380px] h-[380px] rounded-full opacity-25 blur-3xl pointer-events-none"
            style={{
              background:
                "radial-gradient(circle, rgba(255,127,80,0.8), transparent 60%)",
            }}
          />

          <div className="relative grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <span className="font-ui text-xs uppercase tracking-[0.2em] text-[var(--aura-ivory)]/70">
                Community
              </span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05]">
                Join the Aura Circle
              </h2>
              <p className="mt-4 font-body text-[var(--aura-ivory)]/80 max-w-md">
                Get early access to upcoming events, wellness sessions, tech
                meetups, learning workshops, networking evenings, and curated
                community experiences.
              </p>
            </div>

            <div className="lg:col-span-7">
              {success ? (
                <div
                  data-testid={CIRCLE.success}
                  className="rounded-2xl border border-white/15 bg-white/[0.06] p-8"
                >
                  <div className="font-display text-2xl">
                    Welcome to the Aura Circle.
                  </div>
                  <p className="mt-2 font-body text-[var(--aura-ivory)]/80">
                    You&apos;ll receive updates on upcoming Aura events and
                    experiences.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="mt-6 font-ui text-sm underline underline-offset-4"
                  >
                    Add another signup
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid={CIRCLE.form}
                  className="grid sm:grid-cols-2 gap-4"
                  noValidate
                >
                  <FieldDark
                    label="Name"
                    testId={CIRCLE.name}
                    value={state.name}
                    onChange={(v) => setState((s) => ({ ...s, name: v }))}
                    error={errors.name}
                    required
                  />
                  <FieldDark
                    label="Email"
                    type="email"
                    testId={CIRCLE.email}
                    value={state.email}
                    onChange={(v) => setState((s) => ({ ...s, email: v }))}
                    error={errors.email}
                    required
                  />
                  <FieldDark
                    label="Phone / WhatsApp"
                    testId={CIRCLE.phone}
                    value={state.phone}
                    onChange={(v) => setState((s) => ({ ...s, phone: v }))}
                  />
                  <FieldDark
                    label="City"
                    testId={CIRCLE.city}
                    value={state.city}
                    onChange={(v) => setState((s) => ({ ...s, city: v }))}
                  />

                  <div className="sm:col-span-2">
                    <div className="font-ui text-xs uppercase tracking-[0.18em] text-[var(--aura-ivory)]/70">
                      I am interested in
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {INTERESTS.map((int) => {
                        const active = state.interests.includes(int);
                        return (
                          <button
                            type="button"
                            key={int}
                            onClick={() => toggleInterest(int)}
                            data-testid={CIRCLE.interest(slug(int))}
                            className={`font-ui text-xs px-3.5 py-2 rounded-full border transition-all ${
                              active
                                ? "bg-[var(--aura-ivory)] text-[var(--aura-charcoal)] border-[var(--aura-ivory)]"
                                : "bg-transparent text-[var(--aura-ivory)]/85 border-white/20 hover:border-white/50"
                            }`}
                          >
                            {int}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <div className="font-ui text-xs uppercase tracking-[0.18em] text-[var(--aura-ivory)]/70">
                      Preferred format
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {FORMATS.map((f) => {
                        const active = state.preferred_format === f;
                        return (
                          <button
                            type="button"
                            key={f}
                            onClick={() =>
                              setState((s) => ({ ...s, preferred_format: f }))
                            }
                            data-testid={CIRCLE.format(slug(f))}
                            className={`font-ui text-xs px-3.5 py-2 rounded-full border transition-all ${
                              active
                                ? "bg-[var(--aura-ivory)] text-[var(--aura-charcoal)] border-[var(--aura-ivory)]"
                                : "bg-transparent text-[var(--aura-ivory)]/85 border-white/20 hover:border-white/50"
                            }`}
                          >
                            {f}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid={CIRCLE.submit}
                      className="font-ui text-sm px-6 py-3 rounded-full bg-[var(--aura-ivory)] text-[var(--aura-charcoal)] hover:bg-white transition-colors disabled:opacity-60"
                    >
                      {submitting ? "Sending…" : "Join the Aura Circle"}
                    </button>
                    <span className="font-body text-xs text-[var(--aura-ivory)]/60">
                      We&apos;ll only email you about Aura events and experiences.
                    </span>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FieldDark({ label, type = "text", testId, value, onChange, error, required }) {
  return (
    <label className="block">
      <div className="font-ui text-xs uppercase tracking-[0.18em] text-[var(--aura-ivory)]/70">
        {label} {required && <span className="text-[var(--aura-coral)]">*</span>}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`mt-2 w-full bg-transparent border-b ${
          error ? "border-[var(--aura-coral)]" : "border-white/25"
        } focus:border-[var(--aura-ivory)] outline-none py-2.5 font-body text-sm text-[var(--aura-ivory)] placeholder-[var(--aura-ivory)]/40`}
      />
      {error && (
        <div className="mt-1.5 font-body text-xs text-[var(--aura-coral)]">
          {error}
        </div>
      )}
    </label>
  );
}
